import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, Save, AlertCircle } from "lucide-react";
import { portalApi } from "../../lib/portalApi";

const STATUS_OPTIONS = [
  { v: "planning", l: "Planning" },
  { v: "active", l: "Active" },
  { v: "delayed", l: "Delayed" },
  { v: "awaiting_client", l: "Awaiting client" },
  { v: "completed", l: "Completed" },
  { v: "handover", l: "Handover" },
];

/**
 * EditProjectModal — Admin / Project Manager.
 *
 * Edits an existing project's client + project metadata via
 * PATCH /api/portal/projects/{slug}. If client_email changes, the backend
 * surfaces a `client_email_changed` flag and the modal then offers the
 * admin three follow-up choices: update only, resend invite, or revoke
 * old + invite new. Those follow-ups are NOT auto-performed — admin
 * decides per project.
 */
export default function EditProjectModal({ project, onClose, onSaved }) {
  const dialogRef = useRef(null);
  const initial = {
    name: project?.name || "",
    code: project?.code || "",
    client_name: project?.client_name || "",
    client_company: project?.client_company || "",
    client_email: project?.client_email || "",
    client_phone: project?.client_phone || "",
    consultant_name: project?.consultant_name || "",
    consultant_email: project?.consultant_email || "",
    location: project?.location || "",
    project_type: project?.project_type || "",
    start_date: project?.start_date || "",
    target_date: project?.target_date || "",
    status: project?.status || "planning",
    description: project?.description || "",
    notes: project?.notes || "",
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // After save, if the backend reports the client_email changed, show
  // the invite-action prompt instead of closing.
  const [emailChangeInfo, setEmailChangeInfo] = useState(null); // { old, new, savedProject }
  const [actionBusy, setActionBusy] = useState("");
  const [actionMsg, setActionMsg] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy && !emailChangeInfo) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy, emailChangeInfo]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onBackdrop = (e) => {
    if (dialogRef.current && !dialogRef.current.contains(e.target) && !busy && !emailChangeInfo) onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      // Only send fields that actually changed, plus client_email always
      // so the backend can detect a change reliably.
      const diff = {};
      Object.keys(form).forEach((k) => {
        if ((form[k] || "") !== (initial[k] || "")) diff[k] = form[k] || null;
      });
      if (!Object.keys(diff).length) {
        onClose();
        return;
      }
      const resp = await portalApi.updateProject(project.slug, diff);
      const saved = resp.project;
      if (resp.client_email_changed && resp.old_client_email) {
        setEmailChangeInfo({
          old: resp.old_client_email,
          next: saved.client_email,
          savedProject: saved,
        });
        // Don't close — let admin pick a follow-up action.
        return;
      }
      onSaved && onSaved(saved);
      onClose();
    } catch (ex) {
      setErr(ex.message || "Failed to save project");
    } finally {
      setBusy(false);
    }
  };

  // -------- Client email follow-up actions --------
  // We surface 3 admin choices when the client email changes. The actions
  // are implemented client-side because the backend already exposes
  // primitives for them via the existing user/invite endpoints. Admin
  // can also choose "Done" to skip any action.
  const findClientUser = async (email) => {
    if (!email) return null;
    try {
      const { users } = await portalApi.listUsers({ search: email });
      const norm = email.trim().toLowerCase();
      return (users || []).find((u) => (u.email || "").toLowerCase() === norm) || null;
    } catch {
      return null;
    }
  };

  const doResendInviteToNew = async () => {
    if (!emailChangeInfo) return;
    setActionBusy("resend"); setActionMsg("");
    try {
      const user = await findClientUser(emailChangeInfo.next);
      if (!user) {
        setActionMsg(
          `No portal user exists yet for ${emailChangeInfo.next}. Create the user from Users & Access first (Roles → Client), then resend invite.`
        );
        return;
      }
      await portalApi.resendInvite(user.id, { include_password: false });
      setActionMsg(`Invite resent to ${emailChangeInfo.next}.`);
    } catch (ex) {
      setActionMsg(ex.message || "Failed to resend invite");
    } finally {
      setActionBusy("");
    }
  };

  const doRevokeOldAndInviteNew = async () => {
    if (!emailChangeInfo) return;
    setActionBusy("revoke"); setActionMsg("");
    try {
      const oldUser = await findClientUser(emailChangeInfo.old);
      if (oldUser) {
        await portalApi.updateUser(oldUser.id, { active: false });
      }
      const newUser = await findClientUser(emailChangeInfo.next);
      if (newUser) {
        await portalApi.resendInvite(newUser.id, { include_password: false });
        setActionMsg(
          `Disabled old access for ${emailChangeInfo.old}. Invite re-sent to ${emailChangeInfo.next}.`
        );
      } else {
        setActionMsg(
          `Disabled old access for ${emailChangeInfo.old}. New portal user for ${emailChangeInfo.next} doesn't exist yet — create it in Users & Access.`
        );
      }
    } catch (ex) {
      setActionMsg(ex.message || "Failed to update access");
    } finally {
      setActionBusy("");
    }
  };

  const doneEmailChange = () => {
    onSaved && onSaved(emailChangeInfo.savedProject);
    onClose();
  };

  // -------- Render --------

  if (emailChangeInfo) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        data-testid="portal-edit-project-email-prompt"
      >
        <div className="bg-[#FAF5EC] border border-[#6E1F2C]/25 w-full max-w-lg">
          <div className="px-6 py-5 border-b border-[#6E1F2C]/15 bg-white">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-[#A6822F] shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-serif-display text-lg text-[#1C110C]">Client email changed</h3>
                <p className="text-xs text-[#3E2A22] mt-1">
                  From <strong>{emailChangeInfo.old}</strong> to <strong>{emailChangeInfo.next}</strong>.
                </p>
              </div>
            </div>
          </div>
          <div className="px-6 py-5 space-y-3 text-sm text-[#1C110C]">
            <p className="text-[13px]">
              How would you like to handle portal access?
            </p>
            <button
              type="button"
              onClick={doneEmailChange}
              data-testid="portal-edit-project-email-update-only"
              className="w-full text-left bg-white border border-[#6E1F2C]/20 hover:border-[#6E1F2C] px-4 py-3 transition"
            >
              <p className="text-[11px] tracking-[0.22em] uppercase text-[#6E1F2C]">Update email only</p>
              <p className="text-xs text-[#3E2A22] mt-1">
                Project record is updated. No invite is sent. Existing portal user (if any) keeps current access.
              </p>
            </button>
            <button
              type="button"
              onClick={doResendInviteToNew}
              disabled={actionBusy === "resend"}
              data-testid="portal-edit-project-email-resend"
              className="w-full text-left bg-white border border-[#6E1F2C]/20 hover:border-[#6E1F2C] px-4 py-3 transition disabled:opacity-60"
            >
              <p className="text-[11px] tracking-[0.22em] uppercase text-[#6E1F2C] flex items-center gap-2">
                Resend invite to new email
                {actionBusy === "resend" && <Loader2 size={12} className="animate-spin" />}
              </p>
              <p className="text-xs text-[#3E2A22] mt-1">
                Sends a fresh invite email to {emailChangeInfo.next}. Requires that a portal user with that email already exists.
              </p>
            </button>
            <button
              type="button"
              onClick={doRevokeOldAndInviteNew}
              disabled={actionBusy === "revoke"}
              data-testid="portal-edit-project-email-revoke"
              className="w-full text-left bg-white border border-[#A3141A]/30 hover:border-[#A3141A] px-4 py-3 transition disabled:opacity-60"
            >
              <p className="text-[11px] tracking-[0.22em] uppercase text-[#A3141A] flex items-center gap-2">
                Revoke old access & invite new
                {actionBusy === "revoke" && <Loader2 size={12} className="animate-spin" />}
              </p>
              <p className="text-xs text-[#3E2A22] mt-1">
                Disables {emailChangeInfo.old} from logging in, then sends a fresh invite to {emailChangeInfo.next}.
              </p>
            </button>
            {actionMsg && (
              <div
                className="text-xs px-3 py-2 bg-[#FAF5EC] border border-[#6E1F2C]/20 text-[#3E2A22]"
                data-testid="portal-edit-project-email-msg"
              >
                {actionMsg}
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-[#6E1F2C]/10 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={doneEmailChange}
              data-testid="portal-edit-project-email-done"
              className="px-5 py-2 text-[11px] tracking-[0.26em] uppercase bg-[#6E1F2C] hover:bg-[#56151F] text-[#FAF5EC]"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
      onMouseDown={onBackdrop}
      data-testid="portal-edit-project-modal"
    >
      <div
        ref={dialogRef}
        className="bg-[#FAF5EC] border border-[#6E1F2C]/20 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#6E1F2C]/15 bg-white">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Project Setup</p>
            <h2 className="font-serif-display text-xl text-[#1C110C]">Edit project &amp; client details</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="portal-edit-project-close"
            className="text-[#7A6B5F] hover:text-[#6E1F2C] p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-5">
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium mb-3">Project</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Project name" required>
                <input value={form.name} onChange={(e) => setField("name", e.target.value)} required
                  data-testid="portal-edit-project-name" className={inputCls} />
              </Field>
              <Field label="Project code">
                <input value={form.code} onChange={(e) => setField("code", e.target.value)}
                  data-testid="portal-edit-project-code" className={inputCls} />
              </Field>
              <Field label="Project type">
                <input value={form.project_type} onChange={(e) => setField("project_type", e.target.value)}
                  data-testid="portal-edit-project-type" className={inputCls} />
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setField("status", e.target.value)}
                  data-testid="portal-edit-project-status" className={inputCls}>
                  {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </Field>
              <Field label="Location" wide>
                <input value={form.location} onChange={(e) => setField("location", e.target.value)}
                  data-testid="portal-edit-project-location" className={inputCls} />
              </Field>
              <Field label="Start date">
                <input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)}
                  data-testid="portal-edit-project-start" className={inputCls} />
              </Field>
              <Field label="Target / handover date">
                <input type="date" value={form.target_date} onChange={(e) => setField("target_date", e.target.value)}
                  data-testid="portal-edit-project-target" className={inputCls} />
              </Field>
              <Field label="Project description" wide>
                <textarea value={form.description} onChange={(e) => setField("description", e.target.value)}
                  rows={3} data-testid="portal-edit-project-description"
                  className={`${inputCls} resize-none`} />
              </Field>
              <Field label="Internal notes" wide>
                <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)}
                  rows={2} data-testid="portal-edit-project-notes"
                  className={`${inputCls} resize-none`} />
              </Field>
            </div>
          </section>

          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium mb-3">Client</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Client name">
                <input value={form.client_name} onChange={(e) => setField("client_name", e.target.value)}
                  data-testid="portal-edit-project-client-name" className={inputCls} />
              </Field>
              <Field label="Client company">
                <input value={form.client_company} onChange={(e) => setField("client_company", e.target.value)}
                  data-testid="portal-edit-project-client-company" className={inputCls} />
              </Field>
              <Field label="Client email">
                <input type="email" value={form.client_email}
                  onChange={(e) => setField("client_email", e.target.value)}
                  data-testid="portal-edit-project-client-email" className={inputCls} />
              </Field>
              <Field label="Client phone">
                <input type="tel" value={form.client_phone}
                  onChange={(e) => setField("client_phone", e.target.value)}
                  data-testid="portal-edit-project-client-phone" className={inputCls} />
              </Field>
            </div>
          </section>

          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium mb-3">Consultant</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Consultant name">
                <input value={form.consultant_name} onChange={(e) => setField("consultant_name", e.target.value)}
                  data-testid="portal-edit-project-consultant-name" className={inputCls} />
              </Field>
              <Field label="Consultant email">
                <input type="email" value={form.consultant_email}
                  onChange={(e) => setField("consultant_email", e.target.value)}
                  data-testid="portal-edit-project-consultant-email" className={inputCls} />
              </Field>
            </div>
          </section>

          {err && (
            <p className="text-xs text-[#A3141A]" data-testid="portal-edit-project-error">{err}</p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#6E1F2C]/10">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              data-testid="portal-edit-project-cancel"
              className="px-4 py-2 text-[11px] tracking-[0.26em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="portal-edit-project-save"
              className="inline-flex items-center gap-2 bg-[#6E1F2C] hover:bg-[#56151F] text-[#FAF5EC] px-5 py-2 text-[11px] tracking-[0.26em] uppercase disabled:opacity-60"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";

function Field({ label, required, wide, children }) {
  return (
    <label className={`space-y-1 ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">
        {label}{required && <span className="text-[#A3141A] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
